import {
  Box,
  Divider,
  HStack,
  IconButton,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import Layout from "components/Layout";
import { Item, WithId } from "types";
import { useState } from "react";
import { useConfirmation } from "components/hooks/useConfirmation";
import { useNavigate } from "react-router-dom";
import ItemForm from "components/ItemForm";
import { isEmpty } from "lodash";
import { MdViewList, MdEdit, MdDelete, MdPerson } from "react-icons/md";
import { useEntities } from "components/hooks/useEntities";
import { format } from "date-fns";
import { deleteItem } from "helpers/utils";
import ItemAssignmentModal from "components/ItemAssignmentModal";
import Pagination from "components/Pagination";

const SORT = ["-date_created"];
const FIELDS = [
  "*",
  "inventory_items.*",
  "assignments.*",
  "assignments.assigned_to.*",
];
const DEEP = {
  inventory_items: { _limit: 1, _sort: "-created_at" },
  assignments: { _limit: 1, _sort: "-date_created" },
};

const PAGE_SIZE = 25;

export default function Items() {
  const navigate = useNavigate();
  const tableHeaderBackground = useColorModeValue("gray.200", "gray.900");
  const [edit, setEdit] = useState<WithId<Item>>();
  const toast = useToast();
  const { node: Confirmation, open } = useConfirmation();
  const [selectedItem, setSelectedItem] = useState<WithId<Item>>();
  const assignmentModal = useDisclosure();

  const {
    list,
    isLoading,
    pages,
    page,
    setPage,
    findAndUpdate,
    findAndDelete,
  } = useEntities<WithId<Item>>({
    entity: "Item",
    sort: SORT,
    fields: FIELDS,
    deep: DEEP,
    pageSize: PAGE_SIZE,
  });

  const handleDelete = async (id: string) => {
    try {
      await deleteItem<WithId<Item>>("Item", id);
      findAndDelete(id);
      toast({
        title: "Success",
        description: "Item deleted successfully",
        status: "success",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message ?? "Failed to delete item",
        status: "error",
      });
    }
  };

  const getBalanceColor = (balance: number, reorderLevel?: number) => {
    if (!reorderLevel) return undefined;
    if (balance <= reorderLevel) return "red.500";
    if (balance <= reorderLevel * 1.5) return "yellow.500";
    return "green.500";
  };

  return (
    <Layout>
      <Box>
        <ItemForm
          item={edit}
          onClose={() => setEdit(undefined)}
          onSave={(item) => findAndUpdate(item.id, item)}
        />
        <Divider my={2} />
      </Box>

      <Stack height={"100%"} spacing={4}>
        <Text fontSize="xl">Item List</Text>

        {list.length === 0 && !isLoading && (
          <VStack
            alignItems={"center"}
            justifyContent={"center"}
            height={"100%"}
          >
            <Text fontSize="sm">
              There are no items in your inventory. Create one by clicking the
              `New Item` button above.
            </Text>
          </VStack>
        )}
        {isLoading && (
          <VStack
            alignItems={"center"}
            justifyContent={"center"}
            height={"100%"}
          >
            <Spinner />
          </VStack>
        )}
        {!isEmpty(list) && !isLoading && (
          <>
            <TableContainer overflowY={"scroll"} className="tiny-scrollbar">
              <Table colorScheme="teal">
                <Thead
                  position={"sticky"}
                  top={"0px"}
                  zIndex={99}
                  bg={tableHeaderBackground}
                >
                  <Tr>
                    <Th>#</Th>
                    <Th>Name</Th>
                    <Th>Item Type</Th>
                    <Th isNumeric>Reorder Level</Th>
                    <Th isNumeric>Available Units</Th>
                    <Th>Date</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {list.map((item, index) => (
                    <Tr key={item.id}>
                      <Td>{PAGE_SIZE * (page - 1) + index + 1}</Td>
                      <Td>{item.name}</Td>
                      <Td>{item.item_type}</Td>
                      <Td isNumeric>{item.reorder_level}</Td>
                      <Td
                        isNumeric
                        color={getBalanceColor(
                          item?.inventory_items?.[0]?.balance ?? 0,
                          item.reorder_level
                        )}
                      >
                        {item.assignments?.length
                          ? "--"
                          : item?.inventory_items?.[0]?.balance ?? 0}
                      </Td>
                      <Td>
                        {item.date_created
                          ? format(new Date(item.date_created), "yyyy-MM-dd")
                          : "N/A"}
                      </Td>
                      <Td>
                        <HStack>
                          <IconButton
                            aria-label="View More"
                            icon={<MdViewList />}
                            colorScheme="blue"
                            onClick={() => navigate(`/items/${item.id}`)}
                          />
                          <IconButton
                            aria-label="Edit"
                            icon={<MdEdit />}
                            colorScheme="yellow"
                            onClick={() => setEdit(item)}
                          />
                          {item.item_type === "Asset" &&
                            (item?.inventory_items?.[0]?.balance ?? 0) !==
                              0 && (
                              <IconButton
                                aria-label="Assign"
                                icon={<MdPerson />}
                                colorScheme={
                                  item.assignments?.length ? "green" : "gray"
                                }
                                onClick={() => {
                                  setSelectedItem(item);
                                  assignmentModal.onOpen();
                                }}
                              />
                            )}
                          {(item?.inventory_items?.[0]?.balance ?? 0) === 0 && (
                            <IconButton
                              aria-label="Delete"
                              icon={<MdDelete />}
                              colorScheme="red"
                              onClick={() =>
                                open({
                                  title: "Delete Inventory",
                                  description: (
                                    <>
                                      Are you sure you want to delete this item?{" "}
                                      <br />
                                      Serial: {item.name} <br />
                                    </>
                                  ),
                                  onConfirm: () => handleDelete(item.id),
                                  actions: ["Cancel", "Delete"],
                                })
                              }
                            />
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>

            {pages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={pages}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Stack>
      {selectedItem && (
        <ItemAssignmentModal
          isOpen={assignmentModal.isOpen}
          onClose={() => {
            assignmentModal.onClose();
            setSelectedItem(undefined);
          }}
          item={selectedItem}
          onAssign={(val) => {
            // find the selected item in the list and update it
            const itemToUpdate = list.find(
              (item) => item.id === selectedItem.id
            );
            if (itemToUpdate?.assignments) {
              itemToUpdate.assignments = [val, ...itemToUpdate.assignments];
              findAndUpdate(itemToUpdate.id, itemToUpdate);
            }
          }}
        />
      )}
      {Confirmation}
    </Layout>
  );
}
